import { apiFetch, httpError } from '../services/apiFetch';

const API_URL = import.meta.env.VITE_API_URL;

export async function fetchInvoicesByJob(jobId) {
    const res = await apiFetch(`${API_URL}/api/Invoice/GetInvoicesByJob/${jobId}`, { method: 'GET' });
    if (!res.ok) throw await httpError(res, 'Failed to fetch invoices');
    return res.json();
}

export async function createInvoice(body) {
    const res = await apiFetch(`${API_URL}/api/Invoice/CreateInvoice`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    if (!res.ok) throw await httpError(res, 'Failed to create invoice');
    return res.json();
}

export async function updateInvoice(id, body) {
    const res = await apiFetch(`${API_URL}/api/Invoice/UpdateInvoice/${id}`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    if (!res.ok) throw await httpError(res, 'Failed to update invoice');
    return res.json();
}

export async function deleteInvoice(id) {
    const res = await apiFetch(`${API_URL}/api/Invoice/DeleteInvoice/${id}`, { method: 'POST' });
    if (!res.ok) throw await httpError(res, 'Failed to delete invoice');
}

// Pre-save duplicate check — twin of checkExpenseDuplicates. Vendor field is
// `customer` on the revenue side. Pass excludeId when editing.
export async function checkInvoiceDuplicates({ amount, date, customer, excludeId }) {
    const params = new URLSearchParams({ amount: String(amount), date });
    if (customer) params.set('customer', customer);
    if (excludeId != null) params.set('excludeId', String(excludeId));
    const res = await apiFetch(`${API_URL}/api/Invoice/CheckDuplicates?${params}`, { method: 'GET' });
    if (!res.ok) throw await httpError(res, 'Failed to check for duplicates');
    return res.json();
}

// Clears the OCR-set "possible duplicate" flag on a scanned invoice.
export async function dismissInvoiceDuplicate(id) {
    const res = await apiFetch(`${API_URL}/api/Invoice/DismissDuplicate/${id}`, { method: 'POST' });
    if (!res.ok) throw await httpError(res, 'Failed to dismiss duplicate flag');
}

// Fill-manually recovery commit. Client echoes back the pending blob metadata
// from the failed ScanJob alongside the form fields — server copies the blob
// into its final path and writes the InvoiceAttachment row. Revenue-side twin
// of createExpenseFromReceipt.
export async function createInvoiceFromAttachment(body) {
    const res = await apiFetch(`${API_URL}/api/Invoice/CreateInvoiceFromAttachment`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    if (!res.ok) throw await httpError(res, 'Failed to create invoice from attachment');
    return res.json();
}

export async function fetchAttachmentsByInvoice(invoiceId) {
    const res = await apiFetch(`${API_URL}/api/Invoice/GetAttachmentsByInvoice/${invoiceId}`, { method: 'GET' });
    if (!res.ok) throw await httpError(res, 'Failed to fetch invoice attachments');
    return res.json();
}

// `inline=true` returns a SAS URL with Content-Disposition: inline so the
// browser renders it in an <iframe>/<img>. Default (false) backs the explicit
// Download button.
export async function fetchInvoiceAttachmentDownloadUrl(id, { inline = false } = {}) {
    const qs = inline ? '?inline=true' : '';
    const res = await apiFetch(`${API_URL}/api/Invoice/GetAttachmentDownloadUrl/${id}${qs}`, { method: 'GET' });
    if (!res.ok) throw await httpError(res, 'Failed to prepare download');
    return res.json();
}

// Async scan pipeline entry point — revenue twin of queueReceiptScan.
// Uploads to a pending blob and queues a ReceiptScanJob with TargetType=Invoice;
// the shared worker commits an Invoice + InvoiceAttachment when ready,
// surfacing progress through the same UpdateScanJobs SignalR fire.
export async function queueInvoiceScan(jobId, file) {
    const formData = new FormData();
    formData.append('file', file);
    const res = await apiFetch(`${API_URL}/api/Invoice/QueueInvoiceScan?jobId=${jobId}`, {
        method: 'POST',
        body: formData,
    });
    if (!res.ok) throw await httpError(res, 'Failed to queue invoice scan');
    return res.json();
}
